import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Section, Field, Sel } from "@/components/common";

export default function SettingsPage() {
  const { meta, isAdmin, loadMeta } = useAuth();
  const [s, setS] = useState(null);
  const [newStage, setNewStage] = useState("");
  const [newTag, setNewTag] = useState("");
  const [cf, setCf] = useState({ label: "", type: "text" });
  useEffect(() => { setS({ ...meta, stages: meta.stages.filter((x) => !["Won", "Lost"].includes(x)) }); }, [meta]);
  if (!s) return null;
  const save = async () => {
    try { await api.put("/settings", { stuck_days: Number(s.stuck_days), inactivity_days: Number(s.inactivity_days), stages: s.stages, tags: s.tags, custom_fields: s.custom_fields }); await loadMeta(); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const move = (i, d) => { const a = [...s.stages]; [a[i], a[i + d]] = [a[i + d], a[i]]; setS({ ...s, stages: a }); };
  const ro = !isAdmin;
  return (
    <div className="max-w-4xl">
      <PageHeader title="Settings" subtitle={isAdmin ? "Configure automation rules, pipeline stages, tags and custom fields" : "CRM configuration (managed by your admin)"}>
        {isAdmin && <button data-testid="settings-save-button" onClick={save} className="h-9 rounded-md bg-primary px-5 text-sm font-semibold text-white hover:opacity-90">Save settings</button>}
      </PageHeader>
      <div className="space-y-6">
        <Section title="Automation & in-app alerts">
          <div className="grid gap-4 p-4 sm:grid-cols-2">
            <Field label="Deal stuck in a stage after (days)"><input data-testid="settings-stuck-days" type="number" min="1" disabled={ro} className="crm-input" value={s.stuck_days} onChange={(e) => setS({ ...s, stuck_days: e.target.value })} /></Field>
            <Field label="Lead has no activity after (days)"><input data-testid="settings-inactivity-days" type="number" min="1" disabled={ro} className="crm-input" value={s.inactivity_days} onChange={(e) => setS({ ...s, inactivity_days: e.target.value })} /></Field>
            <p className="text-xs text-muted-foreground sm:col-span-2">All notifications stay inside the CRM — no email, SMS or WhatsApp is ever sent.</p>
          </div>
        </Section>
        <Section title="Pipeline stages">
          <div className="space-y-2 p-4" data-testid="settings-stages">
            {s.stages.map((st, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="num w-6 text-xs text-muted-foreground">{i + 1}</span>
                <input disabled={ro} className="crm-input" value={st} onChange={(e) => { const a = [...s.stages]; a[i] = e.target.value; setS({ ...s, stages: a }); }} />
                {isAdmin && <>
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="rounded p-1 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                  <button disabled={i === s.stages.length - 1} onClick={() => move(i, 1)} className="rounded p-1 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                  <button onClick={() => setS({ ...s, stages: s.stages.filter((_, j) => j !== i) })} className="rounded p-1 text-rose-500 hover:bg-rose-50"><X className="h-4 w-4" /></button>
                </>}
              </div>
            ))}
            <p className="pl-8 text-xs text-muted-foreground">+ Won and Lost are fixed closing stages</p>
            {isAdmin && (
              <div className="flex gap-2 pl-8 pt-2">
                <input data-testid="settings-new-stage" className="crm-input" placeholder="New stage name" value={newStage} onChange={(e) => setNewStage(e.target.value)} />
                <button data-testid="settings-add-stage" onClick={() => { if (newStage.trim()) { setS({ ...s, stages: [...s.stages, newStage.trim()] }); setNewStage(""); } }} className="flex h-9 items-center gap-1 rounded-md border px-3 text-sm font-semibold"><Plus className="h-4 w-4" />Add</button>
              </div>
            )}
          </div>
        </Section>
        <Section title="Tags">
          <div className="flex flex-wrap items-center gap-2 p-4">
            {s.tags.map((t) => <span key={t} className="flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold">{t}{isAdmin && <button onClick={() => setS({ ...s, tags: s.tags.filter((x) => x !== t) })}><X className="h-3 w-3" /></button>}</span>)}
            {isAdmin && <><input data-testid="settings-new-tag" className="crm-input h-8 w-36" placeholder="New tag" value={newTag} onChange={(e) => setNewTag(e.target.value)} />
              <button data-testid="settings-add-tag" onClick={() => { if (newTag.trim() && !s.tags.includes(newTag.trim())) { setS({ ...s, tags: [...s.tags, newTag.trim()] }); setNewTag(""); } }} className="h-8 rounded-md border px-3 text-xs font-semibold">Add</button></>}
          </div>
        </Section>
        <Section title="Custom lead fields">
          <div className="space-y-2 p-4" data-testid="settings-custom-fields">
            {s.custom_fields.map((c) => (
              <div key={c.key} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"><span><b>{c.label}</b> <span className="text-xs text-muted-foreground">({c.type})</span></span>
                {isAdmin && <button onClick={() => setS({ ...s, custom_fields: s.custom_fields.filter((x) => x.key !== c.key) })} className="text-rose-500"><X className="h-4 w-4" /></button>}</div>
            ))}
            {!s.custom_fields.length && <p className="text-xs text-muted-foreground">No custom fields yet</p>}
            {isAdmin && (
              <div className="flex gap-2 pt-2">
                <input data-testid="settings-cf-label" className="crm-input" placeholder="Field label e.g. GST Number" value={cf.label} onChange={(e) => setCf({ ...cf, label: e.target.value })} />
                <div className="w-32"><Sel value={cf.type} onChange={(v) => setCf({ ...cf, type: v })} options={["text", "number", "date"]} /></div>
                <button data-testid="settings-cf-add" onClick={() => { if (cf.label.trim()) { setS({ ...s, custom_fields: [...s.custom_fields, { key: cf.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_"), label: cf.label.trim(), type: cf.type }] }); setCf({ label: "", type: "text" }); } }} className="h-9 rounded-md border px-3 text-sm font-semibold">Add</button>
              </div>
            )}
          </div>
        </Section>
        {isAdmin && <DangerZone />}
      </div>
    </div>
  );
}

const DangerZone = () => {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    if (!window.confirm("This will permanently delete ALL leads, calls, meetings, tasks, EOD reports, notifications, activity logs and targets. Continue?")) return;
    setBusy(true);
    try { await api.post("/admin/reset", { confirm: txt }); toast.success("All sales data deleted"); setTxt(""); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-rose-200 bg-white" data-testid="danger-zone">
      <div className="border-b border-rose-100 px-4 py-3"><h3 className="text-sm font-bold text-rose-700">Danger zone — Reset all data</h3></div>
      <div className="space-y-3 p-4">
        <p className="text-sm text-slate-600">Permanently deletes all leads, calls, meetings, tasks, EOD reports, notifications, activity logs and targets. User accounts and settings are kept.</p>
        <div className="flex flex-wrap gap-2">
          <input data-testid="reset-confirm-input" className="crm-input max-w-xs" placeholder='Type DELETE to confirm' value={txt} onChange={(e) => setTxt(e.target.value)} />
          <button data-testid="reset-all-data-button" disabled={txt !== "DELETE" || busy} onClick={reset} className="h-9 rounded-md bg-rose-600 px-4 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-40">{busy ? "Deleting…" : "Reset all data"}</button>
        </div>
      </div>
    </div>
  );
};
